import type { tInspectionFormInput, tMediaInput } from '../types';
import {
    DEFAULT_FORM_INPUT_TYPES,
    FORM_ITEM_CATEGORIES,
    ITEM_STATUS_CODES,
    OPERATOR_RESPONSE_TYPES,
} from './constants';
import { OPERATOR_RESPONSE_VALUES } from './formPromptMapping';

export const DAILY_SERVICE_NEW_ISSUES_LABEL = 'New Issues';
export const DAILY_SERVICE_NEW_ISSUES_RECORD_LABEL = 'Record an issue';
export const DAILY_SERVICE_NEW_ISSUES_NONE_LABEL = 'No new issues';
export const DAILY_SERVICE_NEW_ISSUES_DESCRIPTION =
    'Choose Record an issue to add notes, a photo, video, or audio. Choose No new issues if there is nothing to report.';

export const DAILY_SERVICE_NEW_ISSUES_CHOICES = [
    { value: ITEM_STATUS_CODES.warn, label: DAILY_SERVICE_NEW_ISSUES_RECORD_LABEL },
    { value: OPERATOR_RESPONSE_VALUES.noNewIssues, label: DAILY_SERVICE_NEW_ISSUES_NONE_LABEL },
] as const;

export function isDailyServiceNewIssuesRecording(value: string | undefined): boolean {
    return value === ITEM_STATUS_CODES.warn;
}

export function isDailyServiceNewIssuesPrompt(
    prompt?: Pick<tInspectionFormInput, 'subType' | 'category' | 'label'> | null,
): boolean {
    if (!prompt) return false;
    if (prompt.subType === OPERATOR_RESPONSE_TYPES.NEW_ISSUES) return true;
    if (prompt.category === FORM_ITEM_CATEGORIES.NEW_ISSUES) return true;
    return prompt.label?.trim().toLowerCase() === DAILY_SERVICE_NEW_ISSUES_LABEL.toLowerCase();
}

export function createDailyServiceNewIssuesRuntimePrompt(): tInspectionFormInput {
    return {
        type: DEFAULT_FORM_INPUT_TYPES.section,
        title: DAILY_SERVICE_NEW_ISSUES_LABEL,
        label: DAILY_SERVICE_NEW_ISSUES_LABEL,
        description: DAILY_SERVICE_NEW_ISSUES_DESCRIPTION,
        inputMode: 'text',
        subType: OPERATOR_RESPONSE_TYPES.NEW_ISSUES,
        value: '',
        notes: '',
        photo: [],
        hasActiveDiscrepancy: false,
        discrepancyNotes: '',
        category: FORM_ITEM_CATEGORIES.NEW_ISSUES,
        autoFlag: true,
    };
}

export function ensureDailyServiceNewIssuesPrompt(
    prompts: tInspectionFormInput[],
): tInspectionFormInput[] {
    const existing = prompts.find(isDailyServiceNewIssuesPrompt);
    const rest = prompts.filter((prompt) => !isDailyServiceNewIssuesPrompt(prompt));
    const step = existing
        ? {
              ...createDailyServiceNewIssuesRuntimePrompt(),
              ...existing,
              subType: OPERATOR_RESPONSE_TYPES.NEW_ISSUES,
              category: FORM_ITEM_CATEGORIES.NEW_ISSUES,
              title: existing.title || DAILY_SERVICE_NEW_ISSUES_LABEL,
              label: existing.label || DAILY_SERVICE_NEW_ISSUES_LABEL,
              description: DAILY_SERVICE_NEW_ISSUES_DESCRIPTION,
          }
        : createDailyServiceNewIssuesRuntimePrompt();
    return [...rest, step];
}

export function hasNewIssuesEvidence(
    prompt: Pick<tInspectionFormInput, 'notes'>,
    mediaItems: tMediaInput[] = [],
): boolean {
    if (prompt.notes?.trim()) return true;
    return mediaItems.some((item) => Boolean(item.data?.file) || item.name === 'audio.wav');
}

export function canAdvanceDailyServiceNewIssues(
    prompt: Pick<tInspectionFormInput, 'value' | 'notes'>,
    mediaItems: tMediaInput[] = [],
): boolean {
    if (prompt.value === OPERATOR_RESPONSE_VALUES.noNewIssues) return true;
    return prompt.value === ITEM_STATUS_CODES.warn && hasNewIssuesEvidence(prompt, mediaItems);
}

export function getDailyServiceNewIssuesBlockedReason(
    prompt: Pick<tInspectionFormInput, 'value' | 'notes'>,
    mediaItems: tMediaInput[] = [],
): string | null {
    if (canAdvanceDailyServiceNewIssues(prompt, mediaItems)) return null;
    if (prompt.value === ITEM_STATUS_CODES.warn) {
        return 'Add notes, a photo, video, or audio to continue.';
    }
    return 'Record an issue or choose No new issues to continue.';
}
